import {
  CloseOutlined,
  CopyOutlined,
  LineChartOutlined,
  TableOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Alert, Button, Empty, Spin, Tooltip } from "antd";
import {
  Suspense,
  useMemo,
  useRef,
  type CSSProperties,
  type PointerEvent,
} from "react";
import { useTranslation } from "react-i18next";

import { lazyWithReload } from "@/lib/lazy-with-reload";
import { datasetByKey } from "@/warRoom/datasets/registry";
import type { Row } from "@/warRoom/datasets/types";
import TableView from "@/warRoom/graph/TableView";
import { aggregate } from "@/warRoom/graph/aggregate";
import { formByType } from "@/warRoom/graph/chartForms";
import { formatValue } from "@/warRoom/graph/format";
import { useGraphStore, type Widget } from "@/warRoom/store/useGraphStore";

import { useGuideStore } from "./guides";
import {
  alignMove,
  alignResize,
  clampPos,
  clampSize,
  type Box,
} from "./layout";

const ChartRenderer = lazyWithReload(
  () => import("@/warRoom/graph/ChartRenderer"),
);

/** 標題列的高度，跟 .widget__bar 的 CSS 一致 —— 圖的高度要扣掉它 */
const BAR_H = 36;
const BODY_PAD = 10;

const NO_ROWS: Row[] = [];

/**
 * 畫布上的一張圖卡。
 *
 * 拖曳與縮放中「不寫進 store」：直接改這個 DOM 的 left/top/width/height，
 * 放手才 commit 一次。每個 mousemove 都進 store 會讓底下的 G2 重畫，
 * 卡片一多就拖不動了。
 */
export default function WidgetCard({
  widget,
  selected,
}: {
  widget: Widget;
  selected: boolean;
}) {
  const { t } = useTranslation();
  const elRef = useRef<HTMLDivElement>(null);
  // 動作的 identity 是固定的，用 getState() 取就好 —— 整包訂閱會讓每次 store 變動都重畫圖
  const act = useGraphStore.getState;
  const mode = useGraphStore((s) => s.mode);

  const { spec } = widget;
  const datasetKey = spec.datasetKey;
  const dataset = datasetKey ? datasetByKey(datasetKey) : undefined;
  const rows =
    useGraphStore((s) =>
      datasetKey ? s.rowsByDataset[datasetKey] : undefined,
    ) ?? NO_ROWS;
  const loading = useGraphStore((s) =>
    datasetKey ? !!s.loadingByDataset[datasetKey] : false,
  );
  const fetchError = useGraphStore((s) =>
    datasetKey ? s.errorByDataset[datasetKey] : undefined,
  );

  const result = useMemo(
    () => (dataset ? aggregate(dataset, rows, spec) : null),
    [dataset, rows, spec],
  );

  const form = spec.chartType ? formByType(spec.chartType) : undefined;
  const xLabel =
    dataset?.fields.find((f) => f.key === spec.xField)?.label ?? "—";
  const yLabel = spec.yField
    ? (dataset?.fields.find((f) => f.key === spec.yField)?.label ?? "—")
    : t("warRoom.common.count");

  /* ------------------------------ 拖曳與縮放 ------------------------------ */

  /** 拖曳／縮放共用：pointerdown 起手，改 DOM，pointerup 才寫回 store（滑鼠、觸控、筆都走這條） */
  const startGesture = (
    e: PointerEvent,
    onFrame: (
      dx: number,
      dy: number,
      el: HTMLDivElement,
      free: boolean,
    ) => void,
    onCommit: (dx: number, dy: number, free: boolean) => void,
    cursorClass: string,
  ) => {
    if (e.button !== 0) return;
    const el = elRef.current;
    if (!el) return;
    const node = el; // 給下面的 pointermove 用（函式宣告會被提升，拿不到這裡的 narrowing）
    e.preventDefault();
    e.stopPropagation();
    act().selectWidget(widget.id);

    const startX = e.clientX;
    const startY = e.clientY;
    el.classList.add(cursorClass);
    document.body.classList.add("is-gesturing");

    const finish = (ev: globalThis.PointerEvent) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      node.classList.remove(cursorClass);
      document.body.classList.remove("is-gesturing");
      useGuideStore.getState().setGuides([]);
      onCommit(ev.clientX - startX, ev.clientY - startY, ev.altKey);
    };
    function move(ev: globalThis.PointerEvent) {
      // 拖出視窗或切走時收不到 pointerup，那就把第一個「沒按著鍵」的 pointermove 當放手，
      // 不然卡片會黏在游標上
      if (ev.buttons === 0) {
        finish(ev);
        return;
      }
      onFrame(ev.clientX - startX, ev.clientY - startY, node, ev.altKey);
    }
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
  };

  const otherBoxes = (): Box[] =>
    act()
      .widgets.filter((w) => w.id !== widget.id)
      .map(({ x, y, w, h }) => ({ x, y, w, h }));

  const startDrag = (e: PointerEvent) => {
    // 標題列上的按鈕不是把手
    if ((e.target as HTMLElement).closest("button")) return;
    const others = otherBoxes();
    const place = (dx: number, dy: number, free: boolean) =>
      free
        ? { ...clampPos(widget.x + dx, widget.y + dy), guides: [] }
        : alignMove(
            { x: widget.x + dx, y: widget.y + dy, w: widget.w, h: widget.h },
            others,
          );
    startGesture(
      e,
      (dx, dy, el, free) => {
        const p = place(dx, dy, free);
        el.style.left = `${p.x}px`;
        el.style.top = `${p.y}px`;
        useGuideStore.getState().setGuides(p.guides);
      },
      (dx, dy, free) => {
        const p = place(dx, dy, free);
        act().moveWidget(widget.id, p.x, p.y, !free);
      },
      "is-dragging",
    );
  };

  const startResize = (e: PointerEvent) => {
    const others = otherBoxes();
    const size = (dx: number, dy: number, free: boolean) =>
      free
        ? { ...clampSize(widget.w + dx, widget.h + dy), guides: [] }
        : alignResize(widget, { w: widget.w + dx, h: widget.h + dy }, others);
    startGesture(
      e,
      (dx, dy, el, free) => {
        const s = size(dx, dy, free);
        el.style.width = `${s.w}px`;
        el.style.height = `${s.h}px`;
        useGuideStore.getState().setGuides(s.guides);
      },
      (dx, dy, free) => {
        const s = size(dx, dy, free);
        act().resizeWidget(widget.id, s.w, s.h, !free);
      },
      "is-resizing",
    );
  };

  /* --------------------------------- 內容 --------------------------------- */

  const bodyH = widget.h - BAR_H - BODY_PAD * 2;
  const chartHeight = Math.max(120, bodyH);

  const body = () => {
    if (!dataset || !spec.chartType) {
      return <Empty image={null} description={t("warRoom.widget.noDataset")} />;
    }
    if (fetchError) {
      return (
        <Alert
          type="error"
          showIcon
          message={t("warRoom.widget.fetchFailed")}
          description={fetchError}
        />
      );
    }
    if (!result) return null;
    if (widget.viewMode === "table") {
      return <TableView result={result} chartType={spec.chartType} />;
    }
    return (
      <Suspense
        fallback={
          <div
            style={{
              height: chartHeight,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Spin />
          </div>
        }
      >
        <ChartRenderer
          chartType={spec.chartType}
          result={result}
          mode={mode}
          height={chartHeight}
        />
      </Suspense>
    );
  };

  // 一行講完這張圖的規模，跟原本標題下的那行一樣，只是縮進卡片裡
  const showTotal = spec.aggregation === "sum" || spec.aggregation === "count";
  const total = result ? result.points.reduce((s, p) => s + p.value, 0) : 0;
  const meta = [
    `${xLabel} × ${yLabel}`,
    result
      ? t("warRoom.widget.rows", { n: result.rowCount.toLocaleString() })
      : null,
    showTotal && result ? formatValue(total, result.valueFormat) : null,
  ]
    .filter(Boolean)
    .join("　·　");

  const style: CSSProperties = {
    left: widget.x,
    top: widget.y,
    width: widget.w,
    height: widget.h,
    zIndex: selected ? 3 : 1,
  };

  return (
    <div
      ref={elRef}
      data-widget={widget.id}
      className={`widget${selected ? " is-selected" : ""}`}
      style={style}
      onPointerDown={() => act().selectWidget(widget.id)}
    >
      <div className="widget__bar" onPointerDown={startDrag}>
        <span className="widget__title">
          {dataset?.label ?? t("warRoom.widget.noDatasetPicked")}
          {form ? ` · ${form.label}` : ""}
        </span>
        <span className="widget__meta">{meta}</span>
        {loading ? <Spin size="small" /> : null}
        {result && result.warnings.length > 0 ? (
          <Tooltip
            title={result.warnings.map((w) => (
              <div key={w}>{w}</div>
            ))}
          >
            <WarningOutlined className="widget__warn" />
          </Tooltip>
        ) : null}
        <span className="widget__actions" data-export="skip">
          <Tooltip
            title={
              widget.viewMode === "chart"
                ? t("warRoom.widget.viewTable")
                : t("warRoom.widget.viewChart")
            }
          >
            <Button
              size="small"
              type="text"
              icon={
                widget.viewMode === "chart" ? (
                  <TableOutlined />
                ) : (
                  <LineChartOutlined />
                )
              }
              onClick={() =>
                act().setWidgetViewMode(
                  widget.id,
                  widget.viewMode === "chart" ? "table" : "chart",
                )
              }
            />
          </Tooltip>
          <Tooltip title={t("warRoom.widget.duplicate")}>
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              onClick={() => act().duplicateWidget(widget.id)}
            />
          </Tooltip>
          <Tooltip title={t("warRoom.widget.remove")}>
            <Button
              size="small"
              type="text"
              icon={<CloseOutlined />}
              onClick={() => act().removeWidget(widget.id)}
            />
          </Tooltip>
        </span>
      </div>

      <div
        className="widget__body"
        style={{ opacity: loading ? 0.55 : 1, transition: "opacity .18s" }}
      >
        {body()}
      </div>

      {/* 右下角縮放把手；圖表會跟著卡片重新配置 */}
      <span
        className="widget__resize"
        data-export="skip"
        aria-label={t("warRoom.widget.resize")}
        onPointerDown={startResize}
      />
    </div>
  );
}
